// ---------------------------------------------------------------------------
// Puck Editor Wrappers for Server-Side Renderer Components
// ---------------------------------------------------------------------------
// Visual preview wrappers for components that exist as server-side HTML
// renderers in packages/page-builder/src/renderer/components/.
// Uses CSS variables — no hardcoded colors.
// ---------------------------------------------------------------------------

import { CONTENT_WIDTHS, FONT_SIZE_OPTION_VALUES, SPACING_OPTION_VALUES, spacing } from "@ease/page-builder";
import { createImageField, createStructuredArrayField } from "@/components/pages/puck-authoring-fields";

type BlogPostPreviewItem = Record<string, unknown> & {
  title: string;
  excerpt: string;
  featuredImage: string;
  publishedAt: string;
  authorName: string;
  category: string;
  slug: string;
  readingTimeMinutes: number;
};

type TrackingPlatformPreviewItem = Record<string, unknown> & {
  type: string;
  trackingId: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === "string" ? value.trim() : typeof value === "number" ? String(value) : "";
}

function readNumber(value: unknown, fallback: number): number {
  const parsed = typeof value === "number" ? value : Number.parseInt(readString(value), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeBlogPosts(value: unknown): BlogPostPreviewItem[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (!isRecord(item)) return null;
      return {
        title: readString(item.title),
        excerpt: readString(item.excerpt),
        featuredImage: readString(item.featuredImage),
        publishedAt: readString(item.publishedAt),
        authorName: readString(item.authorName),
        category: readString(item.category),
        slug: readString(item.slug),
        readingTimeMinutes: readNumber(item.readingTimeMinutes, 5),
      };
    })
    .filter((item): item is BlogPostPreviewItem => item !== null);
}

function normalizeTrackingPlatforms(value: unknown): TrackingPlatformPreviewItem[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (!isRecord(item)) return null;
      return {
        type: readString(item.type) || "posthog",
        trackingId: readString(item.trackingId),
      };
    })
    .filter((item): item is TrackingPlatformPreviewItem => item !== null);
}

// ---------------------------------------------------------------------------
// Canonical direct renderer keys
// ---------------------------------------------------------------------------
export const HeroSection = {
  fields: {
    headline: { type: "text", label: "Headline" },
    subheadline: { type: "textarea", label: "Subheadline" },
    ctaText: { type: "text", label: "CTA Text" },
    ctaUrl: { type: "text", label: "CTA URL" },
    backgroundImage: createImageField({ label: "Background image" }),
    backgroundOverlay: { type: "number", label: "Background Overlay" },
    alignment: {
      type: "select",
      label: "Alignment",
      options: [
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
      ],
    },
    minHeight: { type: "text", label: "Minimum Height" },
  },
  defaultProps: {
    headline: "Launch a page that converts",
    subheadline: "Use the canonical published hero renderer so previewed content keeps the same saved component key at publish time.",
    ctaText: "Get Started",
    ctaUrl: "#form",
    backgroundImage: "",
    backgroundOverlay: 40,
    alignment: "center",
    minHeight: "80vh",
  },
  render: ({
    headline,
    subheadline,
    ctaText,
    ctaUrl,
    backgroundImage,
    backgroundOverlay,
    alignment,
    minHeight,
  }: {
    headline: string;
    subheadline: string;
    ctaText: string;
    ctaUrl: string;
    backgroundImage: string;
    backgroundOverlay: number;
    alignment: "left" | "center" | "right";
    minHeight: string;
  }) => (
    <section
      style={{
        position: "relative",
        minHeight,
        borderRadius: "16px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: alignment === "left" ? "flex-start" : alignment === "right" ? "flex-end" : "center",
        padding: `${spacing(8)} ${spacing(4)}`,
        color: "white",
        textAlign: alignment,
        background: backgroundImage
          ? `linear-gradient(rgba(15,23,42,${backgroundOverlay / 100}), rgba(15,23,42,${backgroundOverlay / 100})), url(${backgroundImage}) center/cover`
          : "linear-gradient(135deg, var(--color-primary), var(--color-ai, #7c3aed))",
      }}
    >
      <div style={{ maxWidth: CONTENT_WIDTHS.md }}>
        <h1 style={{ fontSize: FONT_SIZE_OPTION_VALUES.xl, fontWeight: 800, marginBottom: spacing(2) }}>{headline}</h1>
        {subheadline && <p style={{ fontSize: FONT_SIZE_OPTION_VALUES.md, opacity: 0.9, marginBottom: spacing(3) }}>{subheadline}</p>}
        <a href={ctaUrl || "#form"} style={{ display: "inline-block", borderRadius: "999px", background: "white", color: "var(--color-primary)", padding: `${spacing(2)} ${spacing(4)}`, fontWeight: 700, textDecoration: "none" }}>
          {ctaText}
        </a>
      </div>
    </section>
  ),
};

export const PopupTrigger = {
  fields: {
    trigger: {
      type: "select",
      label: "Trigger",
      options: [
        { label: "Exit intent", value: "exit_intent" },
        { label: "Scroll depth", value: "scroll_depth" },
        { label: "Time delay", value: "time_delay" },
      ],
    },
    scrollPercentage: { type: "number", label: "Scroll Percentage" },
    delaySeconds: { type: "number", label: "Delay Seconds" },
    heading: { type: "text", label: "Heading" },
    content: { type: "textarea", label: "Content" },
    ctaText: { type: "text", label: "CTA Text" },
    ctaUrl: { type: "text", label: "CTA URL" },
  },
  defaultProps: {
    trigger: "time_delay",
    scrollPercentage: 50,
    delaySeconds: 10,
    heading: "Before you go",
    content: "Offer a clear next step without interrupting the main page flow.",
    ctaText: "Claim the offer",
    ctaUrl: "#form",
  },
  render: ({ trigger, heading, content, ctaText }: { trigger: string; heading: string; content: string; ctaText: string }) => (
    <div style={{ padding: spacing(4), border: "1px dashed var(--color-border)", borderRadius: "16px", background: "var(--color-muted)" }}>
      <div style={{ maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto", padding: spacing(3), borderRadius: "12px", background: "var(--color-card)", boxShadow: "0 12px 32px rgba(15,23,42,0.12)" }}>
        <div style={{ fontSize: FONT_SIZE_OPTION_VALUES.sm, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-muted-foreground)", marginBottom: spacing(1) }}>
          Popup trigger: {trigger.replace("_", " ")}
        </div>
        <h3 style={{ margin: `0 0 ${spacing(1)}`, color: "var(--color-foreground)" }}>{heading}</h3>
        <p style={{ color: "var(--color-muted-foreground)", marginBottom: spacing(2) }}>{content}</p>
        <button style={{ border: "none", borderRadius: "8px", background: "var(--color-primary)", color: "white", padding: `${spacing(2)} ${spacing(3)}`, fontWeight: 700 }}>
          {ctaText}
        </button>
      </div>
    </div>
  ),
};

export const TrackingPixel = {
  fields: {
    eventName: { type: "text", label: "Event Name" },
    platforms: createStructuredArrayField({
      label: "Tracking platforms",
      itemLabel: "Platform",
      addItemLabel: "Add platform",
      itemFields: [
        {
          key: "type",
          label: "Platform",
          type: "select",
          options: [
            { label: "PostHog", value: "posthog" },
            { label: "Meta Pixel", value: "meta" },
            { label: "Google Analytics", value: "google_analytics" },
            { label: "Google Tag Manager", value: "gtm" },
            { label: "TikTok", value: "tiktok" },
            { label: "Custom script", value: "custom" },
          ],
        },
        { key: "trackingId", label: "Tracking ID", type: "text", placeholder: "GTM-XXXX or pixel ID" },
      ],
      getDefaultItem: () => ({ type: "posthog", trackingId: "" }),
      normalizeValue: normalizeTrackingPlatforms,
    }),
  },
  defaultProps: {
    eventName: "PageView",
    platforms: [{ type: "posthog", trackingId: "" }],
  },
  render: ({ eventName, platforms }: { eventName: string; platforms: unknown }) => {
    const items = normalizeTrackingPlatforms(platforms);
    return (
      <div style={{ padding: spacing(3), borderRadius: "12px", border: "1px solid var(--color-border)", background: "var(--color-card)" }}>
        <div style={{ fontSize: FONT_SIZE_OPTION_VALUES.sm, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-muted-foreground)" }}>Tracking pixel</div>
        <h3 style={{ margin: `${spacing(1)} 0`, color: "var(--color-foreground)" }}>{eventName}</h3>
        <p style={{ margin: 0, color: "var(--color-muted-foreground)" }}>
          Publishes hidden analytics scripts for {items.length || 0} configured platform{items.length === 1 ? "" : "s"}.
        </p>
      </div>
    );
  },
};

export const BlogPostCard = {
  fields: {
    title: { type: "text", label: "Title" },
    excerpt: { type: "textarea", label: "Excerpt" },
    featuredImage: createImageField({ label: "Featured image" }),
    publishedAt: { type: "text", label: "Published date" },
    authorName: { type: "text", label: "Author name" },
    category: { type: "text", label: "Category" },
    slug: { type: "text", label: "Slug or URL" },
    readingTimeMinutes: { type: "number", label: "Reading time minutes" },
    ctaText: { type: "text", label: "CTA Text" },
  },
  defaultProps: {
    title: "How to build a better customer journey",
    excerpt: "A practical article preview that can be published with the canonical blog-post-card renderer.",
    featuredImage: "",
    publishedAt: "2026-05-05",
    authorName: "Editorial team",
    category: "Growth",
    slug: "/blog/customer-journey",
    readingTimeMinutes: 6,
    ctaText: "Read More",
  },
  render: ({ title, excerpt, featuredImage, publishedAt, authorName, category, readingTimeMinutes, ctaText }: BlogPostPreviewItem & { ctaText: string }) => (
    <article style={{ overflow: "hidden", border: "1px solid var(--color-border)", borderRadius: "16px", background: "var(--color-card)", maxWidth: CONTENT_WIDTHS.xs }}>
      {featuredImage ? (
        <img src={featuredImage} alt="" style={{ width: "100%", height: "180px", objectFit: "cover" }} />
      ) : (
        <div style={{ height: "180px", background: "linear-gradient(135deg, var(--color-muted), var(--color-border))" }} />
      )}
      <div style={{ padding: spacing(3) }}>
        {category && <span style={{ color: "var(--color-primary)", fontSize: FONT_SIZE_OPTION_VALUES.sm, fontWeight: 700 }}>{category}</span>}
        <h3 style={{ margin: `${spacing(1)} 0`, color: "var(--color-foreground)" }}>{title}</h3>
        <p style={{ color: "var(--color-muted-foreground)", lineHeight: 1.5 }}>{excerpt}</p>
        <footer style={{ fontSize: FONT_SIZE_OPTION_VALUES.sm, color: "var(--color-muted-foreground)", marginBottom: spacing(2) }}>
          {[publishedAt, authorName, `${readingTimeMinutes} min read`].filter(Boolean).join(" · ")}
        </footer>
        <span style={{ color: "var(--color-primary)", fontWeight: 700 }}>{ctaText}</span>
      </div>
    </article>
  ),
};

export const BlogListingGrid = {
  fields: {
    heading: { type: "text", label: "Heading" },
    posts: createStructuredArrayField({
      label: "Blog posts",
      itemLabel: "Post",
      addItemLabel: "Add post",
      maxItems: 6,
      itemFields: [
        { key: "title", label: "Title", type: "text", placeholder: "Post title" },
        { key: "excerpt", label: "Excerpt", type: "textarea", placeholder: "Short article summary" },
        { key: "featuredImage", label: "Featured image", type: "image", placeholder: "Choose an image" },
        { key: "publishedAt", label: "Published date", type: "text", placeholder: "2026-05-05" },
        { key: "authorName", label: "Author", type: "text", placeholder: "Author name" },
        { key: "category", label: "Category", type: "text", placeholder: "Growth" },
        { key: "slug", label: "Slug", type: "text", placeholder: "article-slug" },
        { key: "readingTimeMinutes", label: "Reading minutes", type: "number", placeholder: "5" },
      ],
      getDefaultItem: () => ({
        title: "",
        excerpt: "",
        featuredImage: "",
        publishedAt: "",
        authorName: "",
        category: "",
        slug: "",
        readingTimeMinutes: 5,
      }),
      normalizeValue: normalizeBlogPosts,
    }),
    columns: {
      type: "select",
      label: "Columns",
      options: [
        { label: "1", value: "1" },
        { label: "2", value: "2" },
        { label: "3", value: "3" },
      ],
    },
    ctaText: { type: "text", label: "CTA Text" },
    baseUrl: { type: "text", label: "Base URL" },
  },
  defaultProps: {
    heading: "Latest Posts",
    posts: [
      {
        title: "Turn more visits into consultations",
        excerpt: "Use this card to feature a relevant article on service and campaign pages.",
        featuredImage: "",
        publishedAt: "2026-05-05",
        authorName: "Editorial team",
        category: "Marketing",
        slug: "turn-visits-into-consultations",
        readingTimeMinutes: 5,
      },
      {
        title: "What to fix before launching a funnel",
        excerpt: "Give readers a second practical next step and keep the grid balanced.",
        featuredImage: "",
        publishedAt: "2026-05-05",
        authorName: "Editorial team",
        category: "Funnels",
        slug: "funnel-launch-checklist",
        readingTimeMinutes: 7,
      },
    ],
    columns: "2",
    ctaText: "Read More",
    baseUrl: "/blog",
  },
  render: ({ heading, posts, columns, ctaText }: { heading: string; posts: unknown; columns: string; ctaText: string }) => {
    const items = normalizeBlogPosts(posts);
    const columnCount = Math.max(1, Math.min(3, Number.parseInt(columns, 10) || 2));
    return (
      <section style={{ padding: `${spacing(4)} 0` }}>
        <h2 style={{ marginBottom: spacing(3), color: "var(--color-foreground)" }}>{heading}</h2>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`, gap: spacing(3) }}>
          {items.map((post, index) => (
            <article key={index} style={{ border: "1px solid var(--color-border)", borderRadius: "14px", background: "var(--color-card)", overflow: "hidden" }}>
              {post.featuredImage ? <img src={post.featuredImage} alt="" style={{ width: "100%", height: "140px", objectFit: "cover" }} /> : <div style={{ height: "140px", background: "var(--color-muted)" }} />}
              <div style={{ padding: spacing(2) }}>
                {post.category && <span style={{ color: "var(--color-primary)", fontSize: FONT_SIZE_OPTION_VALUES.sm, fontWeight: 700 }}>{post.category}</span>}
                <h3 style={{ margin: `${spacing(1)} 0`, color: "var(--color-foreground)" }}>{post.title}</h3>
                <p style={{ color: "var(--color-muted-foreground)", fontSize: FONT_SIZE_OPTION_VALUES.sm }}>{post.excerpt}</p>
                <span style={{ color: "var(--color-primary)", fontWeight: 700 }}>{ctaText}</span>
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  },
};

export const OrderBump = {
  fields: {
    productId: { type: "text", label: "Product ID" },
    headline: { type: "text", label: "Headline" },
    description: { type: "textarea", label: "Description" },
    originalPrice: { type: "text", label: "Original Price" },
    bumpPrice: { type: "text", label: "Bump Price" },
    checkboxLabel: { type: "text", label: "Checkbox Label" },
    imageUrl: createImageField({ label: "Product image" }),
  },
  defaultProps: {
    productId: "offer-setup-kit",
    headline: "Add the setup kit",
    description: "A focused add-on shown only inside funnel checkout steps with a real form surface.",
    originalPrice: "EUR149",
    bumpPrice: "EUR49",
    checkboxLabel: "Yes, add this to my order",
    imageUrl: "",
  },
  render: ({ headline, description, originalPrice, bumpPrice, checkboxLabel, imageUrl }: { headline: string; description: string; originalPrice: string; bumpPrice: string; checkboxLabel: string; imageUrl: string }) => (
    <div style={{ border: "2px solid var(--color-warning, #f59e0b)", borderRadius: "14px", padding: spacing(3), background: "var(--color-card)" }}>
      <div style={{ display: "flex", gap: spacing(2), alignItems: "center" }}>
        {imageUrl && <img src={imageUrl} alt="" style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "10px" }} />}
        <div>
          <h3 style={{ margin: `0 0 ${spacing(1)}`, color: "var(--color-foreground)" }}>{headline}</h3>
          <p style={{ margin: 0, color: "var(--color-muted-foreground)" }}>{description}</p>
          <div style={{ marginTop: spacing(1), fontWeight: 700 }}>
            {originalPrice && <span style={{ textDecoration: "line-through", opacity: 0.6, marginRight: spacing(1) }}>{originalPrice}</span>}
            <span>{bumpPrice}</span>
          </div>
        </div>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: spacing(1), marginTop: spacing(2), color: "var(--color-foreground)" }}>
        <input type="checkbox" /> {checkboxLabel}
      </label>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 1. BookingWidget — Calendar date picker with time slots
// ---------------------------------------------------------------------------
export const BookingWidget = {
  fields: {
    heading: { type: "text", label: "Heading" },
    calendarId: { type: "text", label: "Calendar ID" },
    duration: { type: "number", label: "Duration (min)" },
    bookingUrl: { type: "text", label: "Booking URL" },
    showTimezone: {
      type: "select",
      label: "Show Timezone",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  defaultProps: {
    heading: "Book an Appointment",
    calendarId: "",
    duration: 30,
    bookingUrl: "#",
    showTimezone: "yes",
  },
  render: ({ heading, duration, showTimezone }: { heading: string; duration: number; showTimezone: string }) => (
    <div style={{ padding: spacing(3), maxWidth: CONTENT_WIDTHS.lg, margin: "0 auto" }}>
      <h2 style={{ marginBottom: spacing(2), color: "var(--color-foreground)" }}>{heading}</h2>
      <div style={{ display: "flex", gap: spacing(3), flexWrap: "wrap" as const }}>
        <div style={{ flex: 1, minWidth: "250px", border: "1px solid var(--color-border)", borderRadius: "8px", padding: spacing(2) }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", textAlign: "center" as const }}>
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <div key={d} style={{ fontWeight: "bold", fontSize: "0.75rem", color: "var(--color-muted-foreground)" }}>{d}</div>
            ))}
            {Array.from({ length: 28 }, (_, i) => (
              <div
                key={i}
                style={{
                  padding: "6px",
                  borderRadius: "4px",
                  fontSize: "0.85rem",
                  border: "1px solid var(--color-border)",
                  color: i < 5 ? "var(--color-muted-foreground)" : "var(--color-foreground)",
                  opacity: i < 5 ? 0.4 : 1,
                  background: i === 14 ? "var(--color-primary)" : "transparent",
                  ...(i === 14 ? { color: "white" } : {}),
                }}
              >
                {i + 1}
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: "0 0 180px" }}>
          <h4 style={{ margin: `0 0 ${spacing(1)}`, color: "var(--color-foreground)" }}>{duration} min slots</h4>
          {["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM"].map((t) => (
            <div key={t} style={{ padding: "8px", margin: "4px 0", border: "1px solid var(--color-border)", borderRadius: "4px", fontSize: "0.875rem", color: "var(--color-foreground)" }}>
              {t}
            </div>
          ))}
        </div>
      </div>
      {showTimezone === "yes" && (
        <p style={{ fontSize: FONT_SIZE_OPTION_VALUES.sm, color: "var(--color-muted-foreground)", marginTop: spacing(1) }}>
          Timezone: UTC (detected at runtime)
        </p>
      )}
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 2. CountdownTimer — Deadline countdown with days/hours/min/sec
// ---------------------------------------------------------------------------
export const CountdownTimer = {
  fields: {
    heading: { type: "text", label: "Heading" },
    deadline: { type: "text", label: "Deadline (ISO date)" },
    expiredMessage: { type: "text", label: "Expired Message" },
    variant: {
      type: "select",
      label: "Variant",
      options: [
        { label: "Inline", value: "inline" },
        { label: "Banner", value: "banner" },
      ],
    },
  },
  defaultProps: {
    heading: "Offer ends in:",
    deadline: "2026-12-31T23:59:59Z",
    expiredMessage: "This offer has expired.",
    variant: "inline",
  },
  render: ({ heading, variant }: { heading: string; variant: string }) => (
    <div style={{
      padding: spacing(3),
      textAlign: "center" as const,
      ...(variant === "banner" ? { background: "var(--color-primary)", color: "white", borderRadius: "8px" } : {}),
    }}>
      {heading && <h2 style={{ marginBottom: spacing(2), color: variant === "banner" ? "white" : "var(--color-foreground)" }}>{heading}</h2>}
      <div style={{ display: "flex", justifyContent: "center", gap: spacing(2) }}>
        {[
          { value: "12", label: "Days" },
          { value: "08", label: "Hours" },
          { value: "45", label: "Minutes" },
          { value: "30", label: "Seconds" },
        ].map(({ value, label }) => (
          <div key={label} style={{ textAlign: "center" as const }}>
            <div style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              background: variant === "banner" ? "rgba(255,255,255,0.2)" : "var(--color-muted)",
              borderRadius: "8px",
              padding: "8px 16px",
              color: variant === "banner" ? "white" : "var(--color-foreground)",
            }}>
              {value}
            </div>
            <span style={{ fontSize: "0.75rem", color: variant === "banner" ? "rgba(255,255,255,0.8)" : "var(--color-muted-foreground)" }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 3. CtaBanner — Headline + subtext + call-to-action button
// ---------------------------------------------------------------------------
export const CtaBanner = {
  fields: {
    headline: { type: "text", label: "Headline" },
    subtext: { type: "textarea", label: "Subtext" },
    ctaText: { type: "text", label: "Button Text" },
    ctaUrl: { type: "text", label: "Button URL" },
    variant: {
      type: "select",
      label: "Variant",
      options: [
        { label: "Primary", value: "primary" },
        { label: "Secondary", value: "secondary" },
        { label: "Accent", value: "accent" },
      ],
    },
  },
  defaultProps: {
    headline: "Ready to grow your business?",
    subtext: "Join thousands of professionals who trust our platform.",
    ctaText: "Get Started",
    ctaUrl: "#form",
    variant: "primary",
  },
  render: ({ headline, subtext, ctaText, variant }: { headline: string; subtext: string; ctaText: string; variant: string }) => {
    const bgMap: Record<string, string> = {
      primary: "var(--color-primary)",
      secondary: "var(--color-muted)",
      accent: "var(--color-ai, #7c3aed)",
    };
    const textColor = variant === "secondary" ? "var(--color-foreground)" : "white";
    return (
      <div style={{ padding: `${spacing(6)} ${spacing(3)}`, textAlign: "center" as const, background: bgMap[variant] ?? bgMap.primary, borderRadius: "12px", color: textColor }}>
        <h2 style={{ fontSize: FONT_SIZE_OPTION_VALUES.xl, fontWeight: "bold", marginBottom: spacing(2) }}>{headline}</h2>
        {subtext && <p style={{ marginBottom: spacing(3), opacity: 0.9, maxWidth: CONTENT_WIDTHS.xs, margin: `0 auto ${spacing(3)}` }}>{subtext}</p>}
        <button style={{
          padding: `${spacing(2)} ${spacing(4)}`,
          borderRadius: "8px",
          fontWeight: 600,
          border: "none",
          cursor: "pointer",
          background: variant === "secondary" ? "var(--color-primary)" : "white",
          color: variant === "secondary" ? "white" : "var(--color-primary)",
        }}>
          {ctaText}
        </button>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 4. LeadForm — Simple contact/lead capture form
// ---------------------------------------------------------------------------
export const LeadForm = {
  fields: {
    heading: { type: "text", label: "Heading" },
    submitText: { type: "text", label: "Submit Button Text" },
    successMessage: { type: "text", label: "Success Message" },
    consentText: { type: "text", label: "Consent Text" },
    fieldList: { type: "textarea", label: "Fields (one per line: label | type)" },
  },
  defaultProps: {
    heading: "Get in Touch",
    submitText: "Submit",
    successMessage: "Thank you! We'll be in touch.",
    consentText: "I agree to the privacy policy",
    fieldList: "Full Name | text\nEmail | email\nPhone | tel\nMessage | textarea",
  },
  render: ({ heading, submitText, consentText, fieldList }: { heading: string; submitText: string; consentText: string; fieldList: string }) => {
    const fields = fieldList.split("\n").filter(Boolean).map((line) => {
      const [label, type] = line.split("|").map((s) => s.trim());
      return { label: label ?? "", type: type ?? "text" };
    });
    return (
      <div style={{ padding: `${spacing(4)} ${spacing(3)}`, maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto" }}>
        {heading && <h2 style={{ marginBottom: spacing(2), color: "var(--color-foreground)" }}>{heading}</h2>}
        {fields.map((f) => (
          <div key={f.label} style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, marginBottom: "4px", color: "var(--color-foreground)" }}>{f.label}</label>
            {f.type === "textarea" ? (
              <textarea
                placeholder={f.label}
                rows={3}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: "6px", boxSizing: "border-box" as const, resize: "vertical" as const }}
              />
            ) : (
              <input
                type={f.type}
                placeholder={f.label}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: "6px", boxSizing: "border-box" as const }}
              />
            )}
          </div>
        ))}
        {consentText && (
          <label style={{ display: "flex", gap: "8px", alignItems: "flex-start", fontSize: "0.85rem", color: "var(--color-muted-foreground)", marginBottom: "16px" }}>
            <input type="checkbox" style={{ marginTop: "3px" }} />
            {consentText}
          </label>
        )}
        <button style={{ width: "100%", padding: "10px", background: "var(--color-primary)", color: "white", border: "none", borderRadius: "6px", fontWeight: 600, cursor: "pointer" }}>
          {submitText}
        </button>
      </div>
    );
  },
};


// ---------------------------------------------------------------------------
// 5. LogoCloud — Partner/client logo strip
// ---------------------------------------------------------------------------
export const LogoCloud = {
  fields: {
    heading: { type: "text", label: "Heading" },
    logoCount: { type: "number", label: "Number of Logos" },
    size: {
      type: "select",
      label: "Logo Size",
      options: [
        { label: "Small", value: "sm" },
        { label: "Medium", value: "md" },
        { label: "Large", value: "lg" },
      ],
    },
    grayscale: {
      type: "select",
      label: "Grayscale",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  defaultProps: {
    heading: "Trusted by leading companies",
    logoCount: 5,
    size: "md",
    grayscale: "yes",
  },
  render: ({ heading, logoCount, size, grayscale }: { heading: string; logoCount: number; size: string; grayscale: string }) => {
    const sizeMap: Record<string, string> = { sm: "32px", md: "48px", lg: "64px" };
    const height = sizeMap[size] ?? "48px";
    const count = Math.max(1, Math.min(logoCount, 12));
    return (
      <div style={{ padding: `${spacing(4)} ${spacing(3)}`, textAlign: "center" as const }}>
        {heading && <p style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", marginBottom: "20px", textTransform: "uppercase" as const, letterSpacing: "0.05em" }}>{heading}</p>}
        <div style={{ display: "flex", flexWrap: "wrap" as const, gap: "2rem", alignItems: "center", justifyContent: "center" }}>
          {Array.from({ length: count }, (_, i) => (
            <div
              key={i}
              style={{
                height,
                width: "120px",
                background: "var(--color-muted)",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                color: "var(--color-muted-foreground)",
                filter: grayscale === "yes" ? "grayscale(100%)" : "none",
              }}
            >
              Logo {i + 1}
            </div>
          ))}
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 6. MultiStepForm — Multi-step wizard form
// ---------------------------------------------------------------------------
export const MultiStepForm = {
  fields: {
    heading: { type: "text", label: "Heading" },
    stepCount: { type: "number", label: "Number of Steps" },
    submitText: { type: "text", label: "Submit Button Text" },
    showProgress: {
      type: "select",
      label: "Show Progress",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
    stepTitles: { type: "textarea", label: "Step Titles (one per line)" },
  },
  defaultProps: {
    heading: "Application Form",
    stepCount: 3,
    submitText: "Complete",
    showProgress: "yes",
    stepTitles: "Personal Info\nExperience\nReview & Submit",
  },
  render: ({ heading, stepCount, showProgress, stepTitles }: { heading: string; stepCount: number; showProgress: string; stepTitles: string }) => {
    const titles = stepTitles.split("\n").filter(Boolean);
    const steps = Math.max(1, Math.min(stepCount, 10));
    return (
      <div style={{ padding: `${spacing(4)} ${spacing(3)}`, maxWidth: CONTENT_WIDTHS.sm, margin: "0 auto" }}>
        {heading && <h2 style={{ marginBottom: "16px", color: "var(--color-foreground)" }}>{heading}</h2>}
        {showProgress === "yes" && (
          <div style={{ display: "flex", gap: "4px", marginBottom: "24px" }}>
            {Array.from({ length: steps }, (_, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: "4px",
                  borderRadius: "2px",
                  background: i === 0 ? "var(--color-primary)" : "var(--color-muted)",
                }}
              />
            ))}
          </div>
        )}
        <div style={{ border: "1px solid var(--color-border)", borderRadius: "8px", padding: "24px" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "16px", color: "var(--color-foreground)" }}>
            Step 1: {titles[0] ?? "Step 1"}
          </h3>
          <div style={{ marginBottom: "12px" }}>
            <input placeholder="Field 1" style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: "6px", boxSizing: "border-box" as const }} />
          </div>
          <div style={{ marginBottom: "16px" }}>
            <input placeholder="Field 2" style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: "6px", boxSizing: "border-box" as const }} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button style={{ padding: "8px 20px", background: "var(--color-primary)", color: "white", border: "none", borderRadius: "6px", fontWeight: 500, cursor: "pointer" }}>
              Next
            </button>
          </div>
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 7. NavigationBar — Sticky header with logo, links, CTA
// ---------------------------------------------------------------------------
export const NavigationBar = {
  fields: {
    logoUrl: createImageField({ label: "Logo", helperText: "Upload a logo or choose one from your media library." }),
    ctaText: { type: "text", label: "CTA Button Text" },
    ctaUrl: { type: "text", label: "CTA Button URL" },
    linkList: { type: "textarea", label: "Links (one per line: Label | URL)" },
    sticky: {
      type: "select",
      label: "Sticky",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  defaultProps: {
    logoUrl: "",
    ctaText: "Get Started",
    ctaUrl: "#",
    linkList: "Home | /\nFeatures | #features\nPricing | #pricing\nContact | #contact",
    sticky: "yes",
  },
  render: ({ ctaText, linkList }: { ctaText: string; linkList: string }) => {
    const links = linkList.split("\n").filter(Boolean).map((line) => {
      const [label, url] = line.split("|").map((s) => s.trim());
      return { label: label ?? "", url: url ?? "#" };
    });
    return (
      <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing(2)} ${spacing(3)}`, borderBottom: "1px solid var(--color-border)", background: "var(--color-card)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ width: "32px", height: "32px", background: "var(--color-primary)", borderRadius: "6px" }} />
          <span style={{ fontWeight: 700, color: "var(--color-foreground)" }}>Logo</span>
        </div>
        <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
          {links.map((link) => (
            <span key={link.label} style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", cursor: "pointer" }}>{link.label}</span>
          ))}
          {ctaText && (
            <button style={{ padding: "8px 20px", background: "var(--color-primary)", color: "white", border: "none", borderRadius: "6px", fontWeight: 600, cursor: "pointer", fontSize: "0.875rem" }}>
              {ctaText}
            </button>
          )}
        </div>
      </nav>
    );
  },
};

// ---------------------------------------------------------------------------
// 8. SocialProof — Stats, logos, or badges
// ---------------------------------------------------------------------------
export const SocialProof = {
  fields: {
    heading: { type: "text", label: "Heading" },
    variant: {
      type: "select",
      label: "Variant",
      options: [
        { label: "Stats", value: "stats" },
        { label: "Logos", value: "logos" },
        { label: "Badges", value: "badges" },
      ],
    },
    items: { type: "textarea", label: "Items (one per line: Value | Label)" },
  },
  defaultProps: {
    heading: "By the numbers",
    variant: "stats",
    items: "10,000+ | Happy Customers\n98% | Satisfaction Rate\n50+ | Countries\n24/7 | Support",
  },
  render: ({ heading, variant, items }: { heading: string; variant: string; items: string }) => {
    const parsed = items.split("\n").filter(Boolean).map((line) => {
      const [value, label] = line.split("|").map((s) => s.trim());
      return { value: value ?? "", label: label ?? "" };
    });
    return (
      <div style={{ padding: `${spacing(5)} ${spacing(3)}`, textAlign: "center" as const }}>
        {heading && <h2 style={{ marginBottom: "24px", color: "var(--color-foreground)" }}>{heading}</h2>}
        <div style={{ display: "flex", gap: "2rem", justifyContent: "center", flexWrap: "wrap" as const }}>
          {parsed.map((item, i) => (
            <div key={i} style={{
              padding: variant === "stats" ? "16px 24px" : "12px",
              ...(variant === "stats" ? { minWidth: "120px" } : {}),
            }}>
              {variant === "stats" ? (
                <>
                  <div style={{ fontSize: "1.75rem", fontWeight: "bold", color: "var(--color-primary)" }}>{item.value}</div>
                  <div style={{ fontSize: "0.85rem", color: "var(--color-muted-foreground)" }}>{item.label}</div>
                </>
              ) : (
                <div style={{ width: "80px", height: "80px", background: "var(--color-muted)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", color: "var(--color-muted-foreground)" }}>
                  {item.label || item.value}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 9. ComparisonTable — Feature comparison across plans
// ---------------------------------------------------------------------------
export const ComparisonTable = {
  fields: {
    heading: { type: "text", label: "Heading" },
    columns: { type: "textarea", label: "Columns (one per line, * for highlighted)" },
    rows: { type: "textarea", label: "Rows (one per line: Feature | val1 | val2 | ...)" },
    ctaText: { type: "text", label: "CTA Button Text" },
    ctaUrl: { type: "text", label: "CTA URL" },
  },
  defaultProps: {
    heading: "Compare Plans",
    columns: "Starter\n*Growth\nEnterprise",
    rows: "Contacts | 500 | 5,000 | Unlimited\nUsers | 1 | 5 | Unlimited\nAI Features | no | yes | yes\nPriority Support | no | yes | yes\nCustom Integrations | no | no | yes",
    ctaText: "Choose a plan",
    ctaUrl: "#pricing",
  },
  render: ({ heading, columns, rows }: { heading: string; columns: string; rows: string }) => {
    const cols = columns.split("\n").filter(Boolean).map((c) => ({
      label: c.replace(/^\*/, ""),
      isHighlighted: c.startsWith("*"),
    }));
    const parsedRows = rows.split("\n").filter(Boolean).map((line) => {
      const parts = line.split("|").map((s) => s.trim());
      return { feature: parts[0] ?? "", values: parts.slice(1) };
    });
    return (
      <div style={{ padding: `${spacing(4)} ${spacing(3)}`, overflowX: "auto" as const }}>
        <h2 style={{ textAlign: "center" as const, marginBottom: "24px", color: "var(--color-foreground)" }}>{heading}</h2>
        <table style={{ width: "100%", borderCollapse: "collapse" as const, fontSize: "0.875rem" }}>
          <thead>
            <tr>
              <th style={{ padding: "12px", textAlign: "left" as const, borderBottom: "2px solid var(--color-border)" }} />
              {cols.map((col) => (
                <th key={col.label} style={{
                  padding: "12px",
                  textAlign: "center" as const,
                  borderBottom: "2px solid var(--color-border)",
                  color: col.isHighlighted ? "var(--color-primary)" : "var(--color-foreground)",
                  fontWeight: col.isHighlighted ? 700 : 600,
                }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {parsedRows.map((row) => (
              <tr key={row.feature}>
                <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border)", fontWeight: 500, color: "var(--color-foreground)" }}>{row.feature}</td>
                {row.values.map((val, j) => (
                  <td key={j} style={{ padding: "10px 12px", textAlign: "center" as const, borderBottom: "1px solid var(--color-border)", color: "var(--color-foreground)" }}>
                    {val === "yes" ? "\u2713" : val === "no" ? "\u2717" : val}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 10. EmbedWidget — Iframe embed for third-party tools
// ---------------------------------------------------------------------------
export const EmbedWidget = {
  fields: {
    heading: { type: "text", label: "Heading" },
    embedUrl: { type: "text", label: "Embed URL" },
    title: { type: "text", label: "Iframe Title (a11y)" },
    height: { type: "text", label: "Height" },
    aspectRatio: {
      type: "select",
      label: "Aspect Ratio",
      options: [
        { label: "Auto (fixed height)", value: "auto" },
        { label: "16:9", value: "16:9" },
        { label: "4:3", value: "4:3" },
        { label: "1:1", value: "1:1" },
      ],
    },
  },
  defaultProps: {
    heading: "",
    embedUrl: "",
    title: "Embedded content",
    height: "400px",
    aspectRatio: "auto",
  },
  render: ({ heading, embedUrl, title, height }: { heading: string; embedUrl: string; title: string; height: string }) => {
    // Only allow http(s) URLs in iframe — block javascript:, data:, etc.
    const isSafeUrl = embedUrl && /^https?:\/\//i.test(embedUrl);
    return (
    <div style={{ padding: spacing(3) }}>
      {heading && <h2 style={{ marginBottom: "16px", color: "var(--color-foreground)" }}>{heading}</h2>}
      {isSafeUrl ? (
        <div style={{ width: "100%", height, overflow: "hidden", borderRadius: "8px", border: "1px solid var(--color-border)" }}>
          <iframe
            src={embedUrl}
            title={title}
            style={{ border: 0, width: "100%", height: "100%" }}
            sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
            loading="lazy"
          />
        </div>
      ) : (
        <div style={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed var(--color-border)",
          borderRadius: "8px",
          color: "var(--color-muted-foreground)",
          fontSize: "0.875rem",
        }}>
          {embedUrl && !isSafeUrl ? "Only https:// URLs are allowed" : "Set an embed URL in the sidebar"}
        </div>
      )}
    </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 11. VideoPlayer — Lesson video with progress threshold
// ---------------------------------------------------------------------------
export const VideoPlayer = {
  fields: {
    title: { type: "text", label: "Title" },
    videoUrl: { type: "text", label: "Video URL" },
    sourceType: {
      type: "select",
      label: "Source",
      options: [
        { label: "Auto", value: "auto" },
        { label: "YouTube", value: "youtube" },
        { label: "Vimeo", value: "vimeo" },
        { label: "Bunny", value: "bunny" },
        { label: "Wistia", value: "wistia" },
        { label: "Loom", value: "loom" },
        { label: "R2 / MP4", value: "r2" },
      ],
    },
    completionThreshold: { type: "number", label: "Completion Threshold (%)" },
    showProgress: {
      type: "select",
      label: "Show Progress",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
    autoPlay: {
      type: "select",
      label: "Autoplay",
      options: [
        { label: "No", value: "no" },
        { label: "Yes", value: "yes" },
      ],
    },
    posterUrl: createImageField({ label: "Poster image", helperText: "Optional poster shown before the video starts." }),
  },
  defaultProps: {
    title: "Lesson Video",
    videoUrl: "",
    sourceType: "auto",
    completionThreshold: 90,
    showProgress: "yes",
    autoPlay: "no",
    posterUrl: "",
  },
  render: ({ title, videoUrl, completionThreshold, showProgress }: {
    title: string;
    videoUrl: string;
    completionThreshold: number;
    showProgress: string;
  }) => (
    <div style={{ padding: spacing(3), maxWidth: "760px", margin: "0 auto" }}>
      <div style={{ borderRadius: "16px", overflow: "hidden", border: "1px solid var(--color-border)", background: "var(--color-card)" }}>
        <div style={{ aspectRatio: "16 / 9", display: "grid", placeItems: "center", background: "linear-gradient(135deg, var(--color-primary) 0%, color-mix(in srgb, var(--color-primary) 50%, black) 100%)", color: "white" }}>
          <div style={{ textAlign: "center" as const, padding: spacing(3) }}>
            <div style={{ fontSize: "2rem", marginBottom: "8px" }}>▶</div>
            <p style={{ margin: 0, fontWeight: 600 }}>{title || "Lesson Video"}</p>
            <p style={{ margin: "6px 0 0", opacity: 0.8, fontSize: "0.875rem" }}>{videoUrl || "Set a video URL in the sidebar"}</p>
          </div>
        </div>
        {showProgress === "yes" && (
          <div style={{ padding: "16px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.875rem", marginBottom: "8px", color: "var(--color-foreground)" }}>
              <span>Lesson progress</span>
              <span>{completionThreshold}% to complete</span>
            </div>
            <div style={{ height: "8px", background: "var(--color-muted)", borderRadius: "999px", overflow: "hidden" }}>
              <div style={{ width: "0%", height: "100%", background: "var(--color-primary)" }} />
            </div>
          </div>
        )}
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 12. ProgressBar — Visual completion indicator
// ---------------------------------------------------------------------------
export const ProgressBar = {
  fields: {
    label: { type: "text", label: "Label" },
    currentPercent: { type: "number", label: "Current %" },
    showPercent: {
      type: "select",
      label: "Show Percentage",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
    trackingScope: {
      type: "select",
      label: "Tracking Scope",
      options: [
        { label: "Lesson", value: "lesson" },
        { label: "Module", value: "module" },
        { label: "Course", value: "course" },
        { label: "Custom", value: "custom" },
      ],
    },
  },
  defaultProps: {
    label: "Your Progress",
    currentPercent: 65,
    showPercent: "yes",
    trackingScope: "module",
  },
  render: ({ label, currentPercent, showPercent }: { label: string; currentPercent: number; showPercent: string }) => {
    const pct = Math.max(0, Math.min(100, currentPercent));
    const isComplete = pct >= 100;
    return (
      <div style={{ padding: "16px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--color-foreground)" }}>{label}</span>
          {showPercent === "yes" && (
            <span style={{ fontSize: "0.875rem", fontWeight: 600, color: isComplete ? "var(--color-success, #22c55e)" : "var(--color-primary)" }}>{pct}%</span>
          )}
        </div>
        <div style={{ width: "100%", height: "8px", background: "var(--color-muted)", borderRadius: "4px", overflow: "hidden" }}>
          <div style={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: "4px",
            background: isComplete ? "var(--color-success, #22c55e)" : "var(--color-primary)",
            transition: "width 300ms ease",
          }} />
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 13. DripGate — Conditional course unlock
// ---------------------------------------------------------------------------
export const DripGate = {
  fields: {
    mode: {
      type: "select",
      label: "Mode",
      options: [
        { label: "Immediately", value: "immediately" },
        { label: "Specific Date", value: "date_based" },
        { label: "Days After Enrollment", value: "days_after_enrollment" },
        { label: "Prerequisite Progress", value: "progress_based" },
        { label: "Quiz Score", value: "quiz_score_based" },
      ],
    },
    unlockDate: { type: "text", label: "Unlock Date (ISO)" },
    delayDays: { type: "number", label: "Delay Days" },
    prerequisiteId: { type: "text", label: "Prerequisite ID" },
    prerequisiteType: {
      type: "select",
      label: "Prerequisite Type",
      options: [
        { label: "Lesson", value: "lesson" },
        { label: "Module", value: "module" },
      ],
    },
    requiredQuizId: { type: "text", label: "Required Quiz ID" },
    requiredScore: { type: "number", label: "Required Score (%)" },
    lockedMessage: { type: "text", label: "Locked Message" },
    unlockedMessage: { type: "text", label: "Unlocked Message" },
  },
  defaultProps: {
    mode: "progress_based",
    unlockDate: "",
    delayDays: 7,
    prerequisiteId: "",
    prerequisiteType: "lesson",
    requiredQuizId: "",
    requiredScore: 80,
    lockedMessage: "Complete the previous section to continue.",
    unlockedMessage: "Content unlocked.",
  },
  render: ({ mode, unlockDate, delayDays, prerequisiteId, prerequisiteType, requiredQuizId, requiredScore, lockedMessage, unlockedMessage }: {
    mode: string;
    unlockDate: string;
    delayDays: number;
    prerequisiteId: string;
    prerequisiteType: string;
    requiredQuizId: string;
    requiredScore: number;
    lockedMessage: string;
    unlockedMessage: string;
  }) => {
    const detailMap: Record<string, string> = {
      immediately: "Available immediately.",
      date_based: unlockDate ? `Unlocks on ${unlockDate}.` : "Set an unlock date.",
      days_after_enrollment: `Unlocks ${delayDays} days after enrollment.`,
      progress_based: prerequisiteId ? `Requires ${prerequisiteType} ${prerequisiteId}.` : "Set a prerequisite ID.",
      quiz_score_based: requiredQuizId ? `Requires ${requiredScore}% on quiz ${requiredQuizId}.` : "Set the required quiz ID.",
    };

    return (
      <div style={{ padding: spacing(3), maxWidth: CONTENT_WIDTHS.sm, margin: "0 auto" }}>
        <div style={{ borderRadius: "14px", border: "1px solid var(--color-border)", background: "color-mix(in srgb, var(--color-warning, #f59e0b) 10%, white)", padding: "20px" }}>
          <p style={{ margin: 0, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.75rem", color: "var(--color-muted-foreground)" }}>Course gate</p>
          <h3 style={{ margin: "8px 0 6px", color: "var(--color-foreground)" }}>{lockedMessage}</h3>
          <p style={{ margin: 0, color: "var(--color-muted-foreground)" }}>{detailMap[mode] ?? "Configure this gate."}</p>
          <div style={{ marginTop: "14px", paddingTop: "14px", borderTop: "1px dashed var(--color-border)" }}>
            <span style={{ fontSize: "0.875rem", color: "var(--color-foreground)" }}>{unlockedMessage}</span>
          </div>
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 14. TextBlock — Rich text content section
// ---------------------------------------------------------------------------
export const TextBlock = {
  fields: {
    heading: { type: "text", label: "Heading" },
    content: { type: "textarea", label: "Content" },
    alignment: {
      type: "select",
      label: "Alignment",
      options: [
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
      ],
    },
    maxWidth: { type: "text", label: "Max Width" },
  },
  defaultProps: {
    heading: "About Our Service",
    content: "We provide enterprise-grade solutions designed for businesses of all sizes. Our platform combines powerful automation with an intuitive interface.\n\nWith over a decade of experience, our team delivers results that matter. From day one, you get access to all the tools you need to succeed.",
    alignment: "left",
    maxWidth: CONTENT_WIDTHS.md,
  },
  render: ({ heading, content, alignment, maxWidth }: { heading: string; content: string; alignment: string; maxWidth: string }) => {
    const paragraphs = content.split(/\n\n+/).filter((p) => p.trim().length > 0);
    return (
      <div style={{ textAlign: alignment as "left" | "center" | "right", maxWidth, margin: "0 auto", padding: `${spacing(4)} ${spacing(3)}` }}>
        {heading && <h2 style={{ marginBottom: "16px", color: "var(--color-foreground)" }}>{heading}</h2>}
        {paragraphs.map((para, i) => (
          <p key={i} style={{ lineHeight: 1.7, color: "var(--color-foreground)", marginBottom: "12px" }}>{para.trim()}</p>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 15. QuizBlock — Scored assessment with multiple choice
// ---------------------------------------------------------------------------
export const QuizBlock = {
  fields: {
    title: { type: "text", label: "Quiz Title" },
    description: { type: "textarea", label: "Description" },
    questionList: { type: "textarea", label: "Questions (one per line)" },
    passingScore: { type: "number", label: "Passing Score (%)" },
    maxAttempts: { type: "number", label: "Max Attempts" },
    shuffleQuestions: {
      type: "select",
      label: "Shuffle Questions",
      options: [
        { label: "No", value: "no" },
        { label: "Yes", value: "yes" },
      ],
    },
    shuffleOptions: {
      type: "select",
      label: "Shuffle Options",
      options: [
        { label: "No", value: "no" },
        { label: "Yes", value: "yes" },
      ],
    },
    showExplanations: {
      type: "select",
      label: "Show Explanations",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  defaultProps: {
    title: "Module Quiz",
    description: "Test your knowledge of the concepts covered in this module.",
    questionList: "What is the main outcome of this lesson? | Understand the core concept | Ignore the framework | Skip the practice step | 1",
    passingScore: 70,
    maxAttempts: 3,
    shuffleQuestions: "no",
    shuffleOptions: "no",
    showExplanations: "yes",
  },
  render: ({ title, description, questionList, passingScore, maxAttempts }: { title: string; description: string; questionList: string; passingScore: number; maxAttempts: number }) => (
    <div style={{ padding: `${spacing(4)} ${spacing(3)}`, maxWidth: CONTENT_WIDTHS.sm, margin: "0 auto" }}>
      <div style={{ border: "1px solid var(--color-border)", borderRadius: "12px", padding: "24px", background: "var(--color-card)" }}>
        <h2 style={{ fontSize: "1.25rem", fontWeight: "bold", marginBottom: "8px", color: "var(--color-foreground)" }}>{title}</h2>
        {description && <p style={{ color: "var(--color-muted-foreground)", marginBottom: "20px", fontSize: "0.9rem" }}>{description}</p>}
        <div style={{ display: "flex", gap: "16px", marginBottom: "20px", fontSize: "0.8rem", color: "var(--color-muted-foreground)" }}>
          <span>Pass: {passingScore}%</span>
          <span>Attempts: {maxAttempts}</span>
        </div>
        {/* Sample question preview */}
        <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: "16px" }}>
          <p style={{ fontWeight: 500, marginBottom: "12px", color: "var(--color-foreground)" }}>
            {questionList.split("\n")[0]?.split("|")[0]?.trim() || "Sample question goes here?"}
          </p>
          {(questionList.split("\n")[0]?.split("|").slice(1, -1).map((opt) => opt.trim()).filter(Boolean) || ["Option A", "Option B", "Option C", "Option D"]).map((opt) => (
            <label key={opt} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", marginBottom: "4px", borderRadius: "6px", border: "1px solid var(--color-border)", cursor: "pointer", fontSize: "0.875rem", color: "var(--color-foreground)" }}>
              <input type="radio" name="quiz-preview" style={{ accentColor: "var(--color-primary)" }} />
              {opt}
            </label>
          ))}
        </div>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 16. ExerciseBlock — Instructions + student file upload
// ---------------------------------------------------------------------------
export const ExerciseBlock = {
  fields: {
    title: { type: "text", label: "Exercise Title" },
    instructions: { type: "textarea", label: "Instructions" },
    acceptedFileTypes: { type: "text", label: "Accepted File Types" },
    maxFileSizeMb: { type: "number", label: "Max File Size (MB)" },
    required: {
      type: "select",
      label: "Required",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
    rubric: { type: "textarea", label: "Rubric / Grading Criteria" },
  },
  defaultProps: {
    title: "Practice Exercise",
    instructions: "Complete the following exercise based on what you learned in this lesson. Upload your work when finished.",
    acceptedFileTypes: ".pdf,.doc,.docx,.jpg,.png,.zip",
    maxFileSizeMb: 50,
    required: "yes",
    rubric: "",
  },
  render: ({ title, instructions, acceptedFileTypes, maxFileSizeMb, required, rubric }: { title: string; instructions: string; acceptedFileTypes: string; maxFileSizeMb: number; required: string; rubric: string }) => (
    <div style={{ padding: `${spacing(4)} ${spacing(3)}`, maxWidth: CONTENT_WIDTHS.sm, margin: "0 auto" }}>
      <div style={{ border: "1px solid var(--color-border)", borderRadius: "12px", padding: "24px", background: "var(--color-card)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: "bold", color: "var(--color-foreground)" }}>{title}</h2>
          {required === "yes" && (
            <span style={{ fontSize: "0.75rem", padding: "2px 8px", borderRadius: "4px", background: "var(--color-primary)", color: "white", fontWeight: 600 }}>Required</span>
          )}
        </div>
        <p style={{ color: "var(--color-foreground)", lineHeight: 1.6, marginBottom: "20px", fontSize: "0.9rem" }}>{instructions}</p>
        {rubric && (
          <div style={{ background: "var(--color-muted)", borderRadius: "8px", padding: "12px", marginBottom: "20px" }}>
            <p style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "4px", color: "var(--color-foreground)" }}>Grading Criteria</p>
            <p style={{ fontSize: "0.8rem", color: "var(--color-muted-foreground)", lineHeight: 1.5 }}>{rubric}</p>
          </div>
        )}
        <div style={{
          border: "2px dashed var(--color-border)",
          borderRadius: "8px",
          padding: "24px",
          textAlign: "center" as const,
          color: "var(--color-muted-foreground)",
        }}>
          <p style={{ fontSize: "0.875rem", marginBottom: "4px" }}>Drag and drop files here or click to upload</p>
          <p style={{ fontSize: "0.75rem", marginBottom: "4px" }}>Accepted: {acceptedFileTypes}</p>
          <p style={{ fontSize: "0.75rem" }}>Max {maxFileSizeMb}MB per file</p>
        </div>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// PB-N: Canonical shared-block editor stubs
// ---------------------------------------------------------------------------
// SHARED_BLOCK_REGISTRY exposes ButtonGroup, ServicesList, ContactInfo,
// OpeningHours, and TeamGrid with editorKey set, but the editor never had
// matching Puck Component objects to mount them — failing the
// `editor-puck-config.test.tsx > mounts every canonical shared block`
// assertion since #918. The runtime renderers already live in
// `packages/page-builder/src/renderer/components/`; these editor stubs
// expose a minimal field set + structurally faithful preview so the editor
// surface can drag-drop the blocks.

export const ButtonGroup = {
  fields: {
    buttons: {
      type: "textarea" as const,
      label: "Buttons (one per line: Label | URL | variant)",
    },
    alignment: {
      type: "select" as const,
      label: "Alignment",
      options: [
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
      ],
    },
    orientation: {
      type: "select" as const,
      label: "Orientation",
      options: [
        { label: "Horizontal", value: "horizontal" },
        { label: "Vertical", value: "vertical" },
      ],
    },
    ariaLabel: { type: "text" as const, label: "Accessible label" },
  },
  defaultProps: {
    buttons: "Primary action | / | primary\nSecondary action | /about | secondary",
    alignment: "left",
    orientation: "horizontal",
    ariaLabel: "Actions",
  },
  render: ({ buttons, alignment, orientation, ariaLabel }: {
    buttons: string;
    alignment: string;
    orientation: string;
    ariaLabel: string;
  }) => {
    const items = (buttons ?? "")
      .split("\n")
      .map((line) => line.split("|").map((part) => part.trim()))
      .filter((parts) => parts[0]);
    return (
      <nav
        aria-label={ariaLabel}
        style={{
          display: "flex",
          flexDirection: orientation === "vertical" ? "column" : "row",
          gap: spacing(2),
          justifyContent: alignment === "center" ? "center" : alignment === "right" ? "flex-end" : "flex-start",
          padding: spacing(2),
        }}
      >
        {items.map(([label, , variant], idx) => (
          <span
            key={idx}
            style={{
              padding: `${spacing(1)} ${spacing(3)}`,
              borderRadius: "8px",
              fontWeight: 600,
              background: variant === "primary" ? "var(--color-primary)" : "var(--color-muted)",
              color: variant === "primary" ? "white" : "var(--color-foreground)",
              border: variant === "outline" ? "1px solid var(--color-border)" : "none",
            }}
          >
            {label}
          </span>
        ))}
      </nav>
    );
  },
};

export const ServicesList = {
  // Fields align 1:1 with the runtime renderer's ServicesListProps
  // (packages/page-builder/src/renderer/components/services-list.ts).
  // Earlier draft used `layout` (purely editor-side) which the published
  // renderer ignores — codex P1. Editor must drive published behaviour.
  fields: {
    heading: { type: "text" as const, label: "Heading" },
    services: {
      type: "textarea" as const,
      label: "Services (one per line: Name | Description | Price | Href)",
    },
    columns: { type: "number" as const, label: "Columns (1-4)", min: 1, max: 4 },
    ctaLabel: { type: "text" as const, label: "CTA label" },
  },
  defaultProps: {
    heading: "Services",
    services: "Cleaning | Routine 30-min cleaning | $80\nWhitening | In-office whitening session | $250\nExam | Annual checkup + X-rays | $120",
    columns: 3,
    ctaLabel: "Learn more",
  },
  render: ({ heading, services, columns, ctaLabel }: {
    heading: string;
    services: string;
    columns: number;
    ctaLabel: string;
  }) => {
    const rows = (services ?? "")
      .split("\n")
      .map((line) => line.split("|").map((part) => part.trim()))
      .filter((parts) => parts[0]);
    const cols = Math.min(Math.max(Number(columns) || 3, 1), 4);
    return (
      <section style={{ padding: spacing(4) }}>
        {heading && <h2 style={{ marginBottom: spacing(3), color: "var(--color-foreground)" }}>{heading}</h2>}
        <ul style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: spacing(2),
          listStyle: "none",
          padding: 0,
        }}>
          {rows.map(([name, description, price], idx) => (
            <li key={idx} style={{
              padding: spacing(2),
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
            }}>
              <strong style={{ display: "block", color: "var(--color-foreground)" }}>{name}</strong>
              {description && <p style={{ color: "var(--color-muted-foreground)", fontSize: FONT_SIZE_OPTION_VALUES.sm }}>{description}</p>}
              {price && <span style={{ color: "var(--color-primary)", fontWeight: 700, display: "block" }}>{price}</span>}
              {ctaLabel && <span style={{ color: "var(--color-primary)", fontSize: FONT_SIZE_OPTION_VALUES.sm }}>{ctaLabel}</span>}
            </li>
          ))}
        </ul>
      </section>
    );
  },
};

export const ContactInfo = {
  // Fields align 1:1 with the runtime renderer's ContactInfoProps
  // (packages/page-builder/src/renderer/components/contact-info.ts).
  // Earlier draft used `mapUrl` which the published renderer never reads
  // — codex P2. The runtime renders `website` as the optional outbound
  // link, plus `organization` as the leading line.
  fields: {
    heading: { type: "text" as const, label: "Heading" },
    organization: { type: "text" as const, label: "Organization" },
    address: { type: "textarea" as const, label: "Address" },
    phone: { type: "text" as const, label: "Phone" },
    email: { type: "text" as const, label: "Email" },
    website: { type: "text" as const, label: "Website URL" },
  },
  defaultProps: {
    heading: "Contact",
    organization: "Acme Co",
    address: "123 Main Street\nAustin, TX 78701",
    phone: "(555) 123-4567",
    email: "hello@example.com",
    website: "",
  },
  render: ({ heading, organization, address, phone, email, website }: {
    heading: string;
    organization: string;
    address: string;
    phone: string;
    email: string;
    website: string;
  }) => (
    <section style={{ padding: spacing(4), maxWidth: CONTENT_WIDTHS.md }}>
      {heading && <h2 style={{ marginBottom: spacing(3), color: "var(--color-foreground)" }}>{heading}</h2>}
      <div style={{ display: "grid", gap: spacing(2), color: "var(--color-muted-foreground)" }}>
        {organization && (
          <div>
            <strong style={{ color: "var(--color-foreground)" }}>{organization}</strong>
          </div>
        )}
        {address && (
          <div>
            <strong style={{ color: "var(--color-foreground)" }}>Address</strong>
            <p style={{ whiteSpace: "pre-line", margin: 0 }}>{address}</p>
          </div>
        )}
        {phone && (
          <div>
            <strong style={{ color: "var(--color-foreground)" }}>Phone</strong>
            <p style={{ margin: 0 }}>{phone}</p>
          </div>
        )}
        {email && (
          <div>
            <strong style={{ color: "var(--color-foreground)" }}>Email</strong>
            <p style={{ margin: 0 }}>{email}</p>
          </div>
        )}
        {website && (
          <a href={website} style={{ color: "var(--color-primary)", fontWeight: 600 }}>{website}</a>
        )}
      </div>
    </section>
  ),
};

export const OpeningHours = {
  fields: {
    heading: { type: "text" as const, label: "Heading" },
    hours: {
      type: "textarea" as const,
      label: "Hours (one per line: Day | Open-Close)",
    },
    closedLabel: { type: "text" as const, label: "Closed label" },
    timezone: { type: "text" as const, label: "Timezone label" },
  },
  defaultProps: {
    heading: "Opening hours",
    hours: "Monday | 9:00 AM-5:00 PM\nTuesday | 9:00 AM-5:00 PM\nWednesday | 9:00 AM-5:00 PM\nThursday | 9:00 AM-5:00 PM\nFriday | 9:00 AM-5:00 PM\nSaturday | 10:00 AM-2:00 PM\nSunday | Closed",
    closedLabel: "Closed",
    timezone: "Local time",
  },
  render: ({ heading, hours, closedLabel, timezone }: {
    heading: string;
    hours: string;
    closedLabel: string;
    timezone: string;
  }) => {
    const rows = (hours ?? "")
      .split("\n")
      .map((line) => line.split("|").map((part) => part.trim()))
      .filter((parts) => parts[0]);
    return (
      <section style={{ padding: spacing(4), maxWidth: CONTENT_WIDTHS.sm }}>
        {heading && <h2 style={{ marginBottom: spacing(2), color: "var(--color-foreground)" }}>{heading}</h2>}
        <table style={{ width: "100%", borderCollapse: "collapse" as const }}>
          <tbody>
            {rows.map(([day, range], idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid var(--color-border)" }}>
                <td style={{ padding: spacing(1), color: "var(--color-foreground)", fontWeight: 600 }}>{day}</td>
                <td style={{ padding: spacing(1), color: "var(--color-muted-foreground)", textAlign: "right" as const }}>
                  {range && range.toLowerCase() !== "closed" ? range : closedLabel}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {timezone && <p style={{ marginTop: spacing(2), fontSize: FONT_SIZE_OPTION_VALUES.sm, color: "var(--color-muted-foreground)" }}>{timezone}</p>}
      </section>
    );
  },
};

export const TeamGrid = {
  fields: {
    heading: { type: "text" as const, label: "Heading" },
    members: {
      type: "textarea" as const,
      label: "Members (one per line: Name | Role | Photo URL)",
    },
    columns: { type: "number" as const, label: "Columns (1-4)", min: 1, max: 4 },
  },
  defaultProps: {
    heading: "Meet the team",
    members: "Dr. Sarah Patel | Lead Dentist | \nDr. Marcus Johnson | Orthodontist | \nNina Alvarez | Hygienist | ",
    columns: 3,
  },
  render: ({ heading, members, columns }: {
    heading: string;
    members: string;
    columns: number;
  }) => {
    const rows = (members ?? "")
      .split("\n")
      .map((line) => line.split("|").map((part) => part.trim()))
      .filter((parts) => parts[0]);
    const cols = Math.min(Math.max(Number(columns) || 3, 1), 4);
    return (
      <section style={{ padding: spacing(4) }}>
        {heading && <h2 style={{ marginBottom: spacing(3), color: "var(--color-foreground)", textAlign: "center" as const }}>{heading}</h2>}
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: spacing(3) }}>
          {rows.map(([name, role, photo], idx) => (
            <div key={idx} style={{ textAlign: "center" as const }}>
              {photo ? (
                <img src={photo} alt={name} style={{ width: "120px", height: "120px", borderRadius: "50%", objectFit: "cover" as const, marginBottom: spacing(2) }} />
              ) : (
                <div style={{ width: "120px", height: "120px", borderRadius: "50%", background: "var(--color-muted)", margin: `0 auto ${spacing(2)}` }} />
              )}
              <strong style={{ color: "var(--color-foreground)", display: "block" }}>{name}</strong>
              {role && <span style={{ color: "var(--color-muted-foreground)", fontSize: FONT_SIZE_OPTION_VALUES.sm }}>{role}</span>}
            </div>
          ))}
        </div>
      </section>
    );
  },
};
